import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

/**
 * MetricCard — executive KPI card with comparison & directional trend indicator
 */
export const MetricCard = ({
  title,
  value,
  delta = 0,
  comparisonLabel = 'vs previous period',
  prefix = '',
  suffix = '',
  icon: Icon,
  invertTrendColor = false, // e.g. for Refunds or Return Rate, higher is worse
  timeframe = '',
  loading = false
}) => {
  const isPositive = delta > 0;
  const isNeutral = delta === 0;

  // By default, positive delta is green. If inverted (like refunds), positive delta is red.
  let trendColor = isPositive ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50';
  if (invertTrendColor) {
    trendColor = isPositive ? 'text-rose-600 bg-rose-50' : 'text-emerald-600 bg-emerald-50';
  }
  if (isNeutral) {
    trendColor = 'text-stone-600 bg-stone-50';
  }

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm animate-pulse flex flex-col justify-between h-32">
        <div className="h-4 bg-stone-100 rounded w-1/2" />
        <div className="h-8 bg-stone-100 rounded w-3/4" />
        <div className="h-4 bg-stone-100 rounded w-1/3" />
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm hover:border-stone-300 transition-all">
      <div className="flex items-center justify-between text-stone-500 text-xs font-medium uppercase tracking-wider mb-2">
        <span>{title}</span>
        {Icon && <Icon className="w-4 h-4 text-stone-400" />}
      </div>

      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-2xl font-bold text-stone-900 tracking-tight">
          {prefix}{value}{suffix}
        </span>
        {timeframe && (
          <span className="text-xs text-stone-400 font-normal">({timeframe})</span>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-xs">
        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md font-semibold ${trendColor}`}>
          {isPositive && <ArrowUpRight className="w-3.5 h-3.5" />}
          {!isPositive && !isNeutral && <ArrowDownRight className="w-3.5 h-3.5" />}
          {isNeutral && <Minus className="w-3.5 h-3.5" />}
          {Math.abs(delta)}%
        </span>
        <span className="text-stone-400 truncate">{comparisonLabel}</span>
      </div>
    </div>
  );
};

export default MetricCard;
